import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { tUi } from '../../utils/dq_lc';
import './TutorialSystem.css';

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Split rich content by \n\n into paragraphs; first paragraph is "headline". */
const parseRichContent = (text) =>
    (text || '').split('\n\n').map((p) => p.trim()).filter(Boolean);

// ─── Sub-components ──────────────────────────────────────────────────────────

const ProgressDots = ({ total, current, accentColor }) => {
    if (total <= 1) return null;
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {Array.from({ length: total }).map((_, i) => (
                <span
                    key={i}
                    className={i === current ? 'tutorial-dot-active' : ''}
                    style={{
                        display:       'inline-block',
                        width:          i === current ? 10 : 7,
                        height:         i === current ? 10 : 7,
                        borderRadius:  '50%',
                        backgroundColor:
                            i < current
                                ? accentColor
                                : i === current
                                ? accentColor
                                : 'rgba(255,255,255,0.3)',
                        transition: 'all 0.2s',
                        flexShrink: 0,
                    }}
                />
            ))}
        </div>
    );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const TutorialPopover = ({
    targetElement,
    step,
    currentStepIndex,
    totalSteps,
    config,
    onNext,
    onPrev,
    onSkip,
    onFinish,
    onDoNotShowAgainChange,
}) => {
    const [popoverStyle, setPopoverStyle] = useState({
        top: -9999, left: -9999, opacity: 0, visibility: 'hidden',
    });
    const [doNotShowAgain, setDoNotShowAgain] = useState(
        config?.defaultDoNotShowAgainValue ?? false,
    );
    const popoverRef = useRef(null);
    const history    = useHistory();

    const locale = localStorage.getItem('dq_locale') || 'en-US';

    // ── Resolved per-step options (step overrides config, config overrides default)
    const isDarkTheme      = step.darkTheme      ?? false;
    const isRichContent    = step.richContent     ?? false;
    const showTitle        = step.showTitle       ?? config?.showTitle       ?? true;
    const showDescription  = step.showDescription ?? config?.showDescription ?? true;
    const showStepText     = step.showStepText    ?? config?.showStepText    ?? false;
    const showPrevButton   = step.showPrevButton  ?? config?.showPrevButton  ?? true;
    const showNextButton   = step.showNextButton  ?? config?.showNextButton  ?? true;
    const showCloseIcon    = step.showCloseIcon   ?? config?.showCloseIcon   ?? config?.showSkipButton ?? true;
    const showDNSACheckbox = (config?.showDoNotShowAgainCheckbox !== false) && !isDarkTheme;
    const accentColor      = config?.accentColor  || '#FF6900';

    const title   = tUi(step.titleKey,   locale) || step.titleKey   || '';
    const content = tUi(step.contentKey, locale) || step.contentKey || '';
    const isLastStep = currentStepIndex === totalSteps - 1;

    // ── Colors by theme
    const colors = isDarkTheme
        ? {
            bg:          '#1E1E1E',
            border:      'rgba(255,105,0,0.25)',
            headlineClr: '#FFFFFF',
            bodyClr:     'rgba(255,255,255,0.82)',
            footerBg:    'rgba(255,255,255,0.05)',
            btnFg:       '#FFFFFF',
            btnBg:       accentColor,
            backFg:      'rgba(255,255,255,0.55)',
            shadow:      '0 24px 64px rgba(0,0,0,0.55), 0 4px 16px rgba(0,0,0,0.4)',
          }
        : {
            bg:          '#FFFFFF',
            border:      'rgba(0,0,0,0.08)',
            headlineClr: '#052F44',
            bodyClr:     '#4B5563',
            footerBg:    '#F9FAFB',
            btnFg:       '#FFFFFF',
            btnBg:       accentColor,
            backFg:      '#9CA3AF',
            shadow:      '0 20px 60px rgba(0,0,0,0.18), 0 4px 16px rgba(0,0,0,0.10)',
          };

    // ── Position calculation
    const calculatePosition = useCallback(() => {
        if (!targetElement || !popoverRef.current) return;

        const targetRect  = targetElement.getBoundingClientRect();
        const popoverRect = popoverRef.current.getBoundingClientRect();
        const margin      = 16;
        const placement   = step.placement || 'bottom';
        const W = popoverRect.width;
        const H = popoverRect.height;

        let top, left;

        switch (placement) {
            case 'right':
                top  = targetRect.top + targetRect.height / 2 - H / 2;
                left = targetRect.right + margin;
                break;
            case 'left':
                top  = targetRect.top + targetRect.height / 2 - H / 2;
                left = targetRect.left - W - margin;
                break;
            case 'top':
                top  = targetRect.top - H - margin;
                left = targetRect.left + targetRect.width / 2 - W / 2;
                break;
            case 'top-right':
                top  = targetRect.top - H - margin;
                left = targetRect.right - W;
                break;
            case 'bottom-start':
                top  = targetRect.bottom + margin;
                left = targetRect.left;
                break;
            case 'bottom-end':
                top  = targetRect.bottom + margin;
                left = targetRect.right - W;
                break;
            default: // bottom-center
                top  = targetRect.bottom + margin;
                left = targetRect.left + targetRect.width / 2 - W / 2;
        }

        // ── Viewport clamping
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const pad = 12;

        if (left < pad)           left = pad;
        if (left + W > vw - pad)  left = vw - W - pad;
        if (top < pad)            top  = targetRect.bottom + margin; // flip below
        if (top + H > vh - pad)   top  = targetRect.top - H - margin; // flip above
        if (top < pad)            top  = pad; // last resort

        // Unchanged → no re-render (this runs every frame, below).
        setPopoverStyle((prev) => (prev && prev.opacity === 1 && prev.top === top && prev.left === left ? prev : {
            top,
            left,
            opacity:    1,
            visibility: 'visible',
            position:   'fixed',
            zIndex:     9999,
            width:      320,
            transition: 'top 0.18s ease, left 0.18s ease',
        }));
    }, [targetElement, step]);

    useEffect(() => {
        // Every animation frame while the step is shown: it covers the MUI
        // Drawer's CSS width transition (~225 ms), which a single calculation
        // catches mid-animation, and any later shift the window does not report
        // as a resize or scroll — a scrollbar coming or going as content loads.
        // Same tracking as the overlay's cut-out, so the two stay together.
        let rafId;
        const poll = () => {
            calculatePosition();
            rafId = requestAnimationFrame(poll);
        };
        rafId = requestAnimationFrame(poll);
        return () => cancelAnimationFrame(rafId);
    }, [calculatePosition]);

    // ── Rich content rendering
    const renderContent = () => {
        if (!showDescription) return null;
        if (!isRichContent) {
            return (
                <p style={{
                    margin:     0,
                    color:      colors.bodyClr,
                    fontSize:   14,
                    lineHeight: 1.65,
                }}>
                    {content}
                </p>
            );
        }

        const paragraphs = parseRichContent(content);
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {paragraphs.map((para, i) => (
                    <p key={i} style={{
                        margin:     0,
                        color:      colors.bodyClr,
                        fontSize:   i === 0 ? 16 : 14,
                        fontWeight: i === 0 ? 700 : 400,
                        lineHeight: 1.55,
                        textAlign:  'center',
                    }}>
                        {para}
                    </p>
                ))}
            </div>
        );
    };

    // ── Render
    return (
        <div
            ref={popoverRef}
            className="tutorial-popover"
            style={{
                ...popoverStyle,
                background:   colors.bg,
                border:       `1px solid ${colors.border}`,
                borderRadius: 14,
                boxShadow:    colors.shadow,
                overflow:     'hidden',
                fontFamily:   "'Inter', 'Roboto', system-ui, sans-serif",
            }}
        >
            {/* ── Header strip ─────────────────────────────────────── */}
            <div style={{
                background:    isDarkTheme
                    ? `linear-gradient(135deg, #FF6900 0%, #e55a00 100%)`
                    : `linear-gradient(135deg, #052F44 0%, #0a4a6e 100%)`,
                padding:       '10px 14px',
                display:       'flex',
                alignItems:    'center',
                justifyContent:'space-between',
                minHeight:     40,
            }}>
                {/* Left: progress dots OR step counter */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <ProgressDots
                        total={totalSteps}
                        current={currentStepIndex}
                        accentColor="#FFFFFF"
                    />
                    {showStepText && totalSteps > 1 && (
                        <span style={{
                            color:      'rgba(255,255,255,0.75)',
                            fontSize:   11,
                            fontWeight: 500,
                        }}>
                            {currentStepIndex + 1} / {totalSteps}
                        </span>
                    )}
                </div>

                {/* Right: close icon */}
                {showCloseIcon && (
                    <button
                        onClick={() => onSkip(doNotShowAgain)}
                        aria-label="Close tutorial"
                        style={{
                            background:  'rgba(255,255,255,0.18)',
                            border:      'none',
                            borderRadius:'50%',
                            width:       26,
                            height:      26,
                            cursor:      'pointer',
                            display:     'flex',
                            alignItems:  'center',
                            justifyContent:'center',
                            color:       '#fff',
                            fontSize:    14,
                            fontWeight:  700,
                            flexShrink:  0,
                            transition:  'background 0.15s',
                            lineHeight:  1,
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.3)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.18)'; }}
                    >
                        ✕
                    </button>
                )}
            </div>

            {/* ── Body ─────────────────────────────────────────────── */}
            <div style={{ padding: '18px 18px 14px' }}>
                {showTitle && title && (
                    <h4 style={{
                        margin:       '0 0 10px',
                        color:        colors.headlineClr,
                        fontSize:     15,
                        fontWeight:   700,
                        lineHeight:   1.4,
                        textAlign:    isDarkTheme ? 'center' : 'left',
                    }}>
                        {title}
                    </h4>
                )}
                {renderContent()}
            </div>

            {/* ── Footer ───────────────────────────────────────────── */}
            <div style={{
                background:     colors.footerBg,
                borderTop:      isDarkTheme ? '1px solid rgba(255,255,255,0.08)' : '1px solid #F3F4F6',
                padding:        '10px 14px',
                display:        'flex',
                alignItems:     'center',
                justifyContent: 'space-between',
                gap:            8,
            }}>
                {/* Left: do-not-show-again checkbox */}
                <div style={{ flex: 1 }}>
                    {showDNSACheckbox && (
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                            <input
                                type="checkbox"
                                checked={doNotShowAgain}
                                onChange={(e) => {
                                    const checked = e.target.checked;
                                    setDoNotShowAgain(checked);
                                    if (onDoNotShowAgainChange) onDoNotShowAgainChange(checked);
                                    // Close immediately when user opts out; API already called above
                                    if (checked) onSkip(false);
                                }}
                                style={{ accentColor, width: 13, height: 13, cursor: 'pointer' }}
                            />
                            <span style={{ fontSize: 11, color: isDarkTheme ? 'rgba(255,255,255,0.5)' : '#9CA3AF' }}>
                                {tUi('tutorial.checkbox.doNotShowAgain', locale) || 'Do not show again'}
                            </span>
                        </label>
                    )}
                </div>

                {/* Right: action buttons */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {/* Custom button */}
                    {step.customButton && (
                        <button
                            onClick={() => {
                                if (step.customButton.route)  history.push(step.customButton.route);
                                if (step.customButton.action) step.customButton.action();
                                else                          onSkip(doNotShowAgain);
                            }}
                            style={{
                                background:   'transparent',
                                border:       `1px solid ${isDarkTheme ? 'rgba(255,255,255,0.3)' : '#D1D5DB'}`,
                                borderRadius: 8,
                                padding:      '6px 14px',
                                cursor:       'pointer',
                                fontSize:     13,
                                fontWeight:   500,
                                color:        isDarkTheme ? '#fff' : '#374151',
                                transition:   'all 0.15s',
                            }}
                        >
                            {tUi(step.customButton.labelKey, locale) || step.customButton.labelKey}
                        </button>
                    )}

                    {/* Back button */}
                    {showPrevButton && currentStepIndex > 0 && (
                        <button
                            onClick={onPrev}
                            style={{
                                background:   'transparent',
                                border:       'none',
                                borderRadius: 8,
                                padding:      '6px 10px',
                                cursor:       'pointer',
                                fontSize:     13,
                                fontWeight:   500,
                                color:        colors.backFg,
                                transition:   'color 0.15s',
                            }}
                        >
                            {tUi('tutorial.btn.back', locale) || 'Back'}
                        </button>
                    )}

                    {/* Next / Finish button */}
                    {showNextButton && (
                        <button
                            onClick={() => isLastStep
                                ? (onFinish ? onFinish(doNotShowAgain) : onSkip(doNotShowAgain))
                                : onNext()
                            }
                            style={{
                                background:   colors.btnBg,
                                border:       'none',
                                borderRadius: 8,
                                padding:      '7px 18px',
                                cursor:       'pointer',
                                fontSize:     13,
                                fontWeight:   600,
                                color:        colors.btnFg,
                                boxShadow:    `0 2px 8px ${accentColor}55`,
                                transition:   'opacity 0.15s, transform 0.12s',
                                display:      'flex',
                                alignItems:   'center',
                                gap:          6,
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.opacity   = '0.9'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.opacity   = '1';   e.currentTarget.style.transform = 'translateY(0)';    }}
                        >
                            {isLastStep
                                ? (tUi('tutorial.btn.finish', locale) || 'Done')
                                : (tUi('tutorial.btn.next',   locale) || 'Next')}
                            {!isLastStep && (
                                <span style={{ fontSize: 12, marginLeft: 2 }}>→</span>
                            )}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TutorialPopover;
